import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";

const THINKING_PHRASES = [
  "Thinking...",
  "Looking into that...",
  "One sec...",
  "Working on it...",
  "Checking things out...",
  "Almost there...",
];

const MASCOT_SRC = "/genie-mascots/role/support.png";

interface ThinkingIndicatorProps {
  /** If set, overrides the cycling phrases (e.g. a tool-call status). */
  status?: string | null;
  /** When true, renders only the spinner + text without the avatar bubble wrapper. */
  inline?: boolean;
}

const ThinkingIndicator = ({ status, inline }: ThinkingIndicatorProps) => {
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    if (status) return;
    const interval = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % THINKING_PHRASES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if (!status) setPhraseIndex(0);
  }, [status]);

  const displayText = status || THINKING_PHRASES[phraseIndex];

  if (inline) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1.5">
        <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
        <span key={displayText} className="animate-fade-in">{displayText}</span>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2.5 justify-start">
      <img
        src={MASCOT_SRC}
        className="h-10 w-10 rounded-full object-cover ring-1 ring-primary/20 flex-shrink-0 mt-0.5"
        alt="Help Genie"
      />
      <div className="bg-gray-100 rounded-lg px-4 py-2">
        <div className="flex items-center gap-2 text-base text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
          <span key={displayText} className="animate-fade-in">
            {displayText}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ThinkingIndicator;
